import { z } from "zod";

const envSchema = z.object({
	// App Config
	NODE_ENV: z.enum([
		"development",
		"test",
		"qa",
		"staging",
		"production",
		"local"
	]),
	REACT_APP_API_URL: z.url(),
	PUBLIC_URL: z.string().optional(),
	REACT_APP_JUSFY_URL: z.url(),
	REACT_APP_JUSBILL_URL: z.url(),
	// jusSub é chamado diretamente pelo admin (fora do gateway REACT_APP_API_URL)
	// no fluxo de criação de cliente com checkout real (boleto).
	REACT_APP_JUSSUB_URL: z.url(),

	// Pagar.me
	REACT_APP_PAGARME_V5_ENCRYPTION_KEY: z.string(),
	REACT_APP_PAGARME_ENCRYPTION_KEY: z.string(),

	// Feature Flags
	REACT_APP_FEATURE_FLAG_API_URL: z.url(),
	REACT_APP_FEATURE_FLAG_CLIENT_KEY: z.string(),
	REACT_APP_FEATURE_FLAG_APP_NAME: z.string(),
	REACT_APP_FEATURE_FLAG_REFRESH_INTERVAL: z.coerce
		.number()
		.int()
		.nonnegative(),

	// JusPage
	REACT_APP_JUSPAGE_URL: z.url(),
	// JusZap
	REACT_APP_JUSZAP_URL: z.url(),
	// JusGPT
	REACT_APP_JUSGPT_URL: z.url(),

	// auth-service
	REACT_APP_AUTH_SERVICE_URL: z.url(),

	// Ory Hydra
	REACT_APP_HYDRA_URL: z.url(),
	REACT_APP_HYDRA_CLIENT_ID: z.string(),
	REACT_APP_HYDRA_AUDIENCE: z.string().optional().default("jusfy"),
	REACT_APP_HYDRA_SCOPE: z.string().optional().default("jusfy:app:*"),

	// auth-front
	REACT_APP_AUTH_FRONT_URL: z
		.union([z.url(), z.literal("")])
		.optional()
		.default(""),

	NOTIFY_TECH_CHANNEL: z.union([z.url(), z.literal("")]).optional().default(""),
});

const _env = envSchema.safeParse(process.env);

if (!_env.success) {
	console.error("❌ Invalid environment variables:");
	_env.error.issues.forEach(issue => {
		console.error(`- ${issue.path.join(".")}: ${issue.message}`);
	});
	throw new Error("Invalid environment variables!");
}

const validatedEnv = _env.data;

export const config = deepFreeze({
	app: {
		nodeEnv: validatedEnv.NODE_ENV,
		baseUrl: validatedEnv.REACT_APP_API_URL,
		adminBaseUrl: `${validatedEnv.REACT_APP_API_URL}/admin`,
		publicUrl: validatedEnv.PUBLIC_URL,
		frontUrl: validatedEnv.REACT_APP_JUSFY_URL,
		jusbillUrl: validatedEnv.REACT_APP_JUSBILL_URL
	},
	jussub: {
		apiUrl: validatedEnv.REACT_APP_JUSSUB_URL,
	},
	authService: {
		baseUrl: validatedEnv.REACT_APP_AUTH_SERVICE_URL,
		enableNewAuthService: validatedEnv.REACT_APP_ENABLE_NEW_AUTH_SERVICE,
	},
	pagarme: {
		v5EncryptionKey: validatedEnv.REACT_APP_PAGARME_V5_ENCRYPTION_KEY,
		encryptionKey: validatedEnv.REACT_APP_PAGARME_ENCRYPTION_KEY
	},
	juspage: {
		apiUrl: validatedEnv.REACT_APP_JUSPAGE_URL,
	},
	juszap: {
		apiUrl: validatedEnv.REACT_APP_JUSZAP_URL,
	},
	hydra: {
		url: validatedEnv.REACT_APP_HYDRA_URL,
		clientId: validatedEnv.REACT_APP_HYDRA_CLIENT_ID,
		audience: validatedEnv.REACT_APP_HYDRA_AUDIENCE,
		scope: validatedEnv.REACT_APP_HYDRA_SCOPE,
	},
	authFront: {
		url: validatedEnv.REACT_APP_AUTH_FRONT_URL,
	},
	jusgpt: {
		apiUrl: validatedEnv.REACT_APP_JUSGPT_URL,
	},
	featureFlag: {
		apiUrl: validatedEnv.REACT_APP_FEATURE_FLAG_API_URL,
		clientKey: validatedEnv.REACT_APP_FEATURE_FLAG_CLIENT_KEY,
		appName: validatedEnv.REACT_APP_FEATURE_FLAG_APP_NAME,
		refreshInterval: validatedEnv.REACT_APP_FEATURE_FLAG_REFRESH_INTERVAL
	},

	slack: {
		notifyTechChannel: validatedEnv.NOTIFY_TECH_CHANNEL,
	}
});

function deepFreeze(obj) {
	if (!obj || typeof obj !== "object" || Object.isFrozen(obj)) return obj;
	Object.getOwnPropertyNames(obj).forEach(prop => {
		const value = obj[prop];
		if (value && typeof value === "object") deepFreeze(value);
	});
	return Object.freeze(obj);
}

export const isDevelopment = () => config.app.nodeEnv === "development";
export const isProduction = () => config.app.nodeEnv === "production";
export const isLocal = () => config.app.nodeEnv === "local";
export const isQa = () => config.app.nodeEnv === "qa";
export const isStaging = () => config.app.nodeEnv === "staging";
